import React from 'react';
import autoBind from 'auto-bind';
import PropTypes from 'prop-types';
import { withRouter } from 'next/router';
import Head from 'next/head';
import debounce from 'lodash.debounce';
import { Container, Modal, ModalBody } from 'reactstrap';
import fscreen from 'fscreen';
import { WAIT_FOR_ACTION } from 'redux-wait-for-action';
import { Divider } from 'theme-ui';
import MasonryList from './MasonryList';
import { retrieveFromLocalStorage, saveToLocalStorage, removeFromLocalStorage } from '../utils/utils';
import {
  requestEvent,
  requestMedias,
  requestMedia,
  requestEventAccessWithPassword,
  requirePassword,
  setFilters,
  clearMedias,
  updateEventClearMedias,
  downloadZipFileRequest,
  LOADING_MEDIAS,
} from '../actions/actions';
import GridTile from './event/GridTile';
import EventPasswordModal from './EventPasswordModal';
import MediaOverlay from './event/MediaOverlay';
import EventSlideshow from './event/EventSlideshow';
import { logEvent } from '../utils/analytics';
import { CloseIcon } from './Icons';
import Loading from './event/Loading';
import Header from './event/Header';
import EventToolbar from './event/EventToolbar.jsx';

const { liveGalleryURL } = require('../constants').get();

class Event extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      isEventMounted: false,
      hasMounted: false,
      eventToken: null,
      photos: [],
      filtersAreActive: false, // Whether filters are currently applied, false if displaying all captures
      isModalOpen: false,
      isLoading: false,
    };
    autoBind(this);
    this.delayedResize = debounce(this.onResize.bind(this), 250);
    this.masonryListRef = React.createRef();
    this.heroTitleRef = React.createRef();
  }

  componentDidMount() {
    const { eventNotFound, fullGalleryEnabled, eventId, id } = this.props;

    this.setState({
      hasMounted: true,
      windowSize: {
        h: window.innerHeight,
        w: window.innerWidth,
      },
      scrollY: 0,
    });

    if (!this.props.isServer) {
      this.props.dispatch(requirePassword(eventId));
    }
    if (eventNotFound || !fullGalleryEnabled) {
      window.location = liveGalleryURL;
    }

    // Repack grid items on debounced resize
    window.addEventListener('resize', this.delayedResize);

    // Handle window hash change
    window.addEventListener('hashchange', this.handleHashChange);

    this.fetchMore();
  }

  componentDidUpdate(prevProps, prevState) {
    // PB-7675: only clear body.modal-open on the password-unlock transition
    // (the original reason this was added in PB-1???/e3febd5c, Apr 2020, was to
    // clean up after EventPasswordModal closed). Running it on every update
    // races Reactstrap's body lock for any modal opened inside Event
    // (MediaOverlay, ZIP download, slideshow), which on iOS Chrome shows up as
    // the "scroll once then freeze until you swipe to another capture" bug.
    const justUnlocked = !!prevProps.passwordProtected && !this.props.passwordProtected;
    if (justUnlocked && this.props.eventLoaded) {
      document.body.classList.remove('modal-open');
    }

    // Scroll back to the top of the grid when the media list resets
    const { photoIndex } = this.props;
    if (photoIndex.length === 0 && prevProps.photoIndex.length !== 0) {
      if (this.masonryListRef.current) {
        this.masonryListRef.current.scrollTop = window.innerHeight;
      }
    }

    // Handle password access
    const { passwordToken, eventId, dispatch } = this.props;
    const { isEventMounted } = this.state;
    const localToken = retrieveFromLocalStorage(`eventToken${eventId}`);

    // If token exists, request the event data
    const token = passwordToken?.token || localToken;
    if (token && !isEventMounted) {
      dispatch(requestEvent(eventId));
      saveToLocalStorage(`eventToken${eventId}`, passwordToken.token);
      this.setState({ isEventMounted: true, eventToken: token });
      return;
    }

    // Handle token error
    if (passwordToken?.error && !prevProps.passwordToken?.error) {
      removeFromLocalStorage(`eventToken${eventId}`);
      this.setState({ isEventMounted: false });
    }
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.delayedResize);
    window.removeEventListener('hashchange', this.handleHashChange);
  }

  onResize() {
    this.setState({
      windowSize: {
        h: window.innerHeight,
        w: window.innerWidth,
      },
    });
  }

  async onTileClick(id) {
    logEvent('media', 'open capture modal', `${this.props.eventId}/${id}`);

    await this.setActiveMedia(id);

    this.overlayToggle();
  }

  async setActiveMedia(id) {
    const { eventId, photoIndex, dispatch, filters, hasMore } = this.props;

    if (this.state.buildingItems || this.state.loadingMedia) {
      return;
    }

    this.setState({ loadingMedia: true });

    dispatch(requestMedia(id, eventId));

    // Get next + previous media for overlay
    const currentIndex = photoIndex.indexOf(id);
    let prevId;
    let nextId;
    if (filters.order !== 'ASC') {
      prevId = photoIndex[currentIndex - 1] || photoIndex[photoIndex.length - 1];
      nextId = photoIndex[currentIndex + 1];
      if (!nextId) {
        if (hasMore) {
          nextId = await this.fetchMore().mediaId;
        } else {
          nextId = photoIndex[0];
        }
      }
    } else {
      nextId = photoIndex[currentIndex - 1] || photoIndex[photoIndex.length - 1];
      prevId = photoIndex[currentIndex + 1];
      if (!prevId) {
        if (hasMore) {
          prevId = await this.fetchMore().mediaId;
        } else {
          prevId = photoIndex[0];
        }
      }
    }

    this.setState({
      prevId,
      nextId,
      loadingMedia: false,
    });
  }

  overlayToggle() {
    this.setState((prevState) => ({
      overlayOpen: !prevState.overlayOpen,
    }));
  }

  slideshowToggle() {
    this.setState((prevState) => {
      if (!prevState.slideshowOpen) {
        if (fscreen.fullscreenEnabled) {
          fscreen.requestFullscreen(document.documentElement);
        }
        document.documentElement.classList.add('modal-open');
        window.location.hash = 'slideshow';
      } else {
        if (fscreen.fullscreenEnabled && fscreen.fullscreenElement !== null) {
          fscreen.exitFullscreen();
        }
        document.documentElement.classList.remove('modal-open');
        window.location.hash = '';
      }

      return {
        slideshowOpen: !prevState.slideshowOpen,
      };
    });
  }

  toggle() {
    this.setState({
      isModalOpen: !this.state.isModalOpen,
    });
  }

  downloadCapturesZipFile() {
    if (this.state.isLoading) {
      return;
    }
    this.setState({ isLoading: true });
    const action = downloadZipFileRequest(this.props.eventId, this.props.title, this.props.captureKinds);
    action[WAIT_FOR_ACTION] = (action) => action.type === 'DOWNLOAD_ZIP_FILE_SUCCESS';
    this.props
      .dispatch(action)
      .then(() => {
        this.toggle();
        this.setState({ isLoading: false });
      })
      .catch((e) => {
        this.setState({ isLoading: false });
        throw new Error(e);
      });
  }

  handleHashChange() {
    if (!window.location.hash && this.state.slideshowOpen) {
      this.slideshowToggle();
    }
  }

  onFilterClick(filters) {
    this.setState({ filtersAreActive: true });
    this.props.dispatch(setFilters(filters));

    setTimeout(this.resetGrid, 1);
  }

  onClearFilter() {
    this.setState({ filtersAreActive: false });
  }

  // Reset the media grid and scroll back to the top
  resetGrid() {
    this.setState({
      photos: [],
      buildingItems: false,
    });

    // Clear media list in store.
    // Start with "event clear medias" as it clears data that would
    // cause the loading of a second page otherwise.
    // Only after that we can dispatch "clear medias",
    // which can trigger the load of the first page.
    this.props.dispatch(updateEventClearMedias(this.props.eventId));
    this.props.dispatch(clearMedias());
  }

  preload(element) {
    const media = element;
    return new Promise((resolve, reject) => {
      const filetype = media.src.split('.').pop();
      let type = 'image';
      if (['mp4', 'mov', 'm4v'].includes(filetype)) {
        type = 'video';
      }

      media.renderType = type;

      let img = new Image();
      img.src = media.src;

      if (type === 'video') {
        img.src = media.static;
      }

      const poll = setInterval(() => {
        if (img.naturalHeight) {
          clearInterval(poll);
          media.size = {
            h: img.naturalHeight,
            w: img.naturalWidth,
          };
          img = null;
          resolve(media);
        }
      }, 30);

      img.onerror = () => {
        clearInterval(poll);
        img = null;
        reject();
      };
    });
  }

  async fetchMore(callback) {
    // Return early if already building a page, loading initial page, or no more items exist
    if (this.state.buildingItems || !this.props.hasMore || this.props.loadingMedias) {
      return;
    }
    this.setState({ buildingItems: true });

    const filter = {
      order: 'DESC', // Include `order` to make sure this object isn't ignored in the saga if missing other props
      ...(this.props.filters || {}),
      captureKind: this.props.captureKinds || [],
    };

    // Request next page of media and wait for the results
    const action = requestMedias(this.props.eventId, null, null, filter);
    action[WAIT_FOR_ACTION] = (action) => action.type === 'LOADING_MEDIAS' && !action.value;
    this.props.dispatch(action).then(() => {
      const photos = Object.values(this.props.photos);
      const stateIds = this.state.photos.map((photo) => photo.id);

      // Build array of media to preload before building next page
      const manifest = [];
      photos.forEach((photo) => {
        if (photo.src && !stateIds.includes(photo.id)) {
          manifest.push(this.preload(photo));
        }
      });

      // Preload the manifest, filtering out any that had an error
      Promise.all(manifest.map((p) => p.catch(() => undefined))).then((result) => {
        this.setState({
          buildingItems: false,
          photos: this.state.photos.concat(result.filter((e) => e)),
        });
        // Run callback function if it exists
        if (callback) {
          callback();
        }
        // Return first photo from newly-loaded results
        return result[0];
      });
    });
  }

  handleEventPassword(password) {
    this.setState({ isEventMounted: false });
    this.props.dispatch(requestEventAccessWithPassword(this.props.eventId, password));
  }

  renderGridItems() {
    return this.state.photos.map((image, index) => (
      <GridTile key={image.id} image={image} onClick={this.onTileClick} />
    ));
  }

  render() {
    const {
      eventLoaded,
      hasMore,
      fullGalleryEnabled,
      passwordProtected,
      passwordToken,
      beta,
      loadingEvent,
      activeMedia,
      currentPlan,
      filters,
    } = this.props;

    const { hint, error } = passwordToken;
    const isPro = currentPlan === 'Pro';
    if (!passwordProtected) {
      let classEventPage = 'eventpage';
      if (beta) {
        classEventPage = 'eventpage-beta';
      }

      let noPhotosString = isPro ? this.props.emptyStateText : 'Nothing to see here yet.\nCheck back soon!';
      if (this.state.filtersAreActive) {
        noPhotosString = 'Nothing could be found matching those filters.';
      }

      if (this.state.windowSize) {
        const numColumns =
          this.state.windowSize.w > 1919
            ? 5
            : this.state.windowSize.w > 1439
            ? 4
            : this.state.windowSize.w > 767
            ? 3
            : 2;

        return (
          eventLoaded &&
          fullGalleryEnabled && (
            <>
              <Head>
                <meta name="theme-color" content={this.props.accentColor} />
              </Head>
              <div style={{ height: '100vh' }}>
                <MasonryList
                  ref={this.masonryListRef}
                  items={
                    this.state.photos?.map((photo, index) => ({
                      id: photo.id || index,
                      width: photo.size.w,
                      height: photo.size.h,
                      component: <GridTile key={photo.id} image={photo} onClick={this.onTileClick} />,
                    })) ?? []
                  }
                  numColumns={numColumns}
                  gap={24}
                  onEndReached={hasMore && this.fetchMore}
                  isLoading={this.state.buildingItems || loadingEvent.loading}
                  ListEmptyComponent={
                    hasMore ? null : (
                      <div className="no-photos" style={{ margin: '0 auto', textAlign: 'center' }}>
                        <img
                          className="no-photos__img"
                          alt=""
                          src={
                            this.props.emptyStateImageUrl && isPro ? this.props.emptyStateImageUrl : '/static/frame.svg'
                          }
                        />
                        <p>{noPhotosString}</p>
                      </div>
                    )
                  }
                >
                  <Header eventId={this.props.eventId} titleRef={this.heroTitleRef} />
                  <EventToolbar
                    heroTitleRef={this.heroTitleRef}
                    onFiltersApplied={this.onFilterClick}
                    onSlideshowPress={this.slideshowToggle}
                    onDownloadPress={this.toggle}
                    onClearFilter={this.onClearFilter}
                    isDownloadZipDisabled={!this.state.photos.length}
                    isSticky
                  />
                </MasonryList>

                <MediaOverlay
                  onClose={this.overlayToggle}
                  overlayOpen={this.state.overlayOpen}
                  buildingItems={this.state.buildingItems}
                  prevId={this.state.prevId}
                  nextId={this.state.nextId}
                  setActiveMedia={this.setActiveMedia}
                />
                <Modal isOpen={this.state.isModalOpen} toggle={this.toggle}>
                  <button type="button" className="modal-close" onClick={this.toggle}>
                    <CloseIcon />
                  </button>
                  <ModalBody>
                    <Container>
                      <h2 className="modal-text">Download All Captures</h2>
                      <Divider mb={4} />
                      <p>
                        Are you sure you want to download a ZIP archive containing all captures from{' '}
                        <strong>{this.props.eventTitle}</strong> gallery?
                      </p>
                      {this.state.isLoading && <Loading loading={this.state.isLoading} />}
                      <button
                        disabled={this.state.isLoading}
                        onClick={this.downloadCapturesZipFile}
                        className="btn"
                        style={{
                          backgroundColor: this.props.accentColor || '#000',
                          color: this.props.contrastColor || '#fff',
                          opacity: this.state.isLoading ? 0.5 : 1,
                          cursor: this.state.isLoading ? 'not-allowed' : 'pointer',
                        }}
                      >
                        {this.state.isLoading ? 'PREPARING ZIP…' : 'DOWNLOAD ZIP'}
                      </button>
                    </Container>
                  </ModalBody>
                </Modal>
              </div>

              {this.state.slideshowOpen && <EventSlideshow onClose={this.slideshowToggle} />}
            </>
          )
        );
      }
      return <div></div>;
    }
    if (this.state.hasMounted && !loadingEvent.loading) {
      return (
        <div>
          <EventPasswordModal
            name="gallery"
            label="Enter Gallery"
            hint={hint}
            error={error}
            submitEventPassword={(password) => this.handleEventPassword(password)}
          />
        </div>
      );
    }

    return <div></div>;
  }
}

Event.propTypes = {
  eventId: PropTypes.string.isRequired,
  dispatch: PropTypes.func.isRequired,
  background: PropTypes.string,
  accentColor: PropTypes.string,
  photos: PropTypes.objectOf(
    PropTypes.shape({
      src: PropTypes.string.isRequired,
      id: PropTypes.string.isRequired,
    })
  ).isRequired,
  loading: PropTypes.bool.isRequired,
  hasMore: PropTypes.bool.isRequired,
  eventLoaded: PropTypes.bool.isRequired,
  pageType: PropTypes.string.isRequired,
  emptyStateText: PropTypes.string,
  emptyStateImageUrl: PropTypes.string,
};

export { Event };
export default withRouter(Event);
